//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import angular, {
  IDocumentService,
  IRootScopeService,
  IScope,
  ITemplateCacheService,
} from 'angular';
import { INgRedux } from 'ng-redux';
import { PojoSet } from 'pojo-sets';
import Redux from 'redux';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import template from '@legacy-components/common/image-uploader/image-uploader.html';
import LEGACY_TOAST_TEMPLATE from '@legacy-components/common/toast-notifications/templates/legacy-toast.html';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import {
  ALTERNATE_TOASTS,
  LEGACY_TOAST_OPTIONS,
} from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import {
  IToastNotification,
  ToastType,
} from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { UploadQueueActions } from '@media-library/redux/upload-queue/uploadQueueSlice';
import { ICuralateStore } from '@models/AppState';
import CollectionId from '@models/CollectionId';
import { IOnChangesObject } from '@models/IOnChangesObject';
import ItemId from '@models/ItemId';
import {
  IQueuedUploader,
  IQueuedUploaderService,
} from '@react-bridge/wrappers/ng-service-provider-types';
import { CollectionsSettingsService as ICollectionsSettingsService } from '@services/collections-settings-service';
import { SessionVarsService } from '@services/session-vars-service';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';
import { isUndefined } from '@utils/filterPredicates';
import createUUID from '@utils/uuid';

import gaApi from 'app/initialization/ga/ga-api';
// eslint-disable-next-line import/no-duplicates
import { GatewayMediaUploadApiService } from 'app/services/gateway-media-upload-api-service';
// eslint-disable-next-line import/no-duplicates
import 'app/services/gateway-media-upload-api-service';

/**
 * A nonfunctional version of the legacy toast service, to be passed into
 * the Queued-Uploader-Service in the case that we don't want toast notifications.
 */
const noopToastNotificationService = {
  info: () => undefined,
  success: () => undefined,
  close: () => undefined,
  warning: () => undefined,
  updateOptions: () => undefined,
  error: () => undefined,
};

interface IImageUploaderFile extends File {
  message?: string;
  uuid?: string;
}

export interface IBroadcastEventForSuccessAndFailed {
  id?: string;
  uuid?: string;
}

export interface IBroadcastEventForProgress {
  loaded: number;
  total: number;
}

export type MediaUploadBroadcastEvent =
  | IBroadcastEventForProgress
  | IBroadcastEventForSuccessAndFailed
  | string;

export interface ImageUploader
  extends ReturnType<(typeof ImageUploader)['mapStateToThis']>,
    ReturnType<(typeof ImageUploader)['mapDispatchToThis']> {}

export class ImageUploader {
  private static readonly mapStateToThis = (state: ICuralateStore) => ({
    isShareV2Vendor: state.session.client.isShareV2Vendor,
    isBazaarvoiceSyndicationEnabled:
      getIsBazaarvoiceNetworkSyndicationFrontendEnabled(state),
    isContentDrawerDroppableLayerActive:
      state.contentDrawer.drawerSettings.isContentDrawerDroppableLayerActive,
  });

  private static readonly mapDispatchToThis = (dispatch: Redux.Dispatch) => ({
    addToastNotification: (toast: IToastNotification) =>
      dispatch(toastNotificationsActions.addToastNotification(toast)),
  });

  /** Bindings */
  public mode?: 'overlay' | 'button-short';
  public imageUploadStartingCallbackAsync?: (params: {
    imageFiles: File[];
  }) => Promise<unknown>;
  public imageUploader?: () => IQueuedUploader;
  public useNoopToastNotificationService?: boolean;
  public name?: string;
  public buttonClass?: string;
  public legacyDarkOverlay?: boolean;

  public file?: File;
  public isDragOver = false;

  private readonly unsubscribe: () => void;
  private readonly uploader: IQueuedUploader;
  private processingVideosIdSet: PojoSet<ItemId> = PojoSet.empty();
  private readonly clearImageWatcherDeregistration?: () => void;
  private videoProcessingWatcherDeregistration?: () => void;

  constructor(
    private readonly $ngRedux: INgRedux,
    private readonly $document: IDocumentService,
    private readonly $rootScope: IRootScopeService,
    private readonly $scope: IScope,
    $templateCache: ITemplateCacheService,
    private readonly CollectionsSettingsService: ICollectionsSettingsService,
    QueuedUploaderService: IQueuedUploaderService,
    private readonly gatewayMediaUploadApiService: GatewayMediaUploadApiService,
    private readonly sessionVarsService: SessionVarsService,
  ) {
    this.unsubscribe = this.$ngRedux.connect(
      ImageUploader.mapStateToThis,
      ImageUploader.mapDispatchToThis,
    )(this);

    // Default the name to an empty string so it is interpolated correctly in the template.
    this.name = this.name ?? '';

    // Share users should be shown the legacy styling, even if we forgot to set this flag.
    if (
      isUndefined(this.legacyDarkOverlay) &&
      this.isShareV2Vendor &&
      !this.isBazaarvoiceSyndicationEnabled
    ) {
      this.legacyDarkOverlay = true;
    }

    // Preloads the templates for the legacy toast notifications used in this component.
    $templateCache.put(
      'directives/toast/simplebar.html',
      LEGACY_TOAST_TEMPLATE,
    );

    if (this.name) {
      this.clearImageWatcherDeregistration = this.$scope.$on(
        `image-uploader:${this.name}:clear-file`,
        () => {
          this.file = undefined;
        },
      );
    }

    /**
     * If we are given an imageUploader immediately, we'll use that as the uploader
     * throughout the lifetime of this component. Otherwise, we'll make a new one.
     */
    if (this.imageUploader) {
      this.uploader = this.imageUploader();
    } else {
      this.uploader = QueuedUploaderService.createUploader({
        uploadDelegate: this.defaultUploadDelegate,
        onItemUploaded: this.defaultOnImageUploadSucceeded,
        onItemFailed: this.defaultOnImageUploadFailed,
        toastNotificationService: this.useNoopToastNotificationService
          ? noopToastNotificationService
          : undefined,
        waitForServerResponse: false,
      });
    }

    // If we're in overlay mode, set up watchers to handle the drag events.
    if (this.mode === 'overlay') {
      $document.on('dragover', (e) => {
        const isSupportedContent = this.isSupportedContent(
          e.originalEvent as DragEvent,
        );

        /**
         * Ignore any dragover events that occur while a modal is covering the screen or
         * that contain unsupported content.
         */
        if (e.target.matches('.modal, .modal *') || !isSupportedContent) {
          return;
        }

        this.isDragOver = !this.isContentDrawerDroppableLayerActive;

        // Open the ContentDrawer to show uploading UI
        $rootScope.$broadcast(
          'universal-upload:open-content-drawer',
          /* stickyDrawer */ false,
        );
        gaApi.recordEvent(
          gaApi.CATEGORY_UNIVERSAL_UPLOADER,
          gaApi.ACTION_OPEN_DRAWER,
          'Overlay',
        );

        $scope.$apply();
      });

      $document.on('mouseleave', () => {
        this.isDragOver = false;
        this.$rootScope.$broadcast(
          'universal-upload:close-content-drawer',
          /* forceClose*/ false,
        );
        $scope.$apply();
      });
    }
  }

  public $onChanges(changesObj: IOnChangesObject<ImageUploader>) {
    if (changesObj.useNoopToastNotificationService) {
      if (this.useNoopToastNotificationService) {
        this.uploader.updateToastNotificationService(
          noopToastNotificationService,
        );
      } else {
        this.uploader.updateToastNotificationService();
      }
    }
  }

  public $onDestroy() {
    this.unsubscribe();

    // If we're in overlay mode, remove the drag event watchers.
    if (this.mode === 'overlay') {
      this.$document.off('dragover');
      this.$document.off('mouseleave');
    }

    this.clearImageWatcherDeregistration?.();
    this.videoProcessingWatcherDeregistration?.();
  }

  public async onFileSelect(files: File[]) {
    this.isDragOver = false;

    this.$rootScope.$broadcast('universal-upload:files-selected', files.length);

    const trackingLabel = (() => {
      switch (this.mode) {
        case 'overlay':
          return 'Overlay';
        case 'button-short':
          return 'DirectUpload';
        default:
          return undefined;
      }
    })();

    if (trackingLabel) {
      gaApi.recordEvent(
        gaApi.CATEGORY_UNIVERSAL_UPLOADER,
        gaApi.ACTION_UPLOAD,
        trackingLabel,
        files.length.toString(),
      );
    }

    if (this.imageUploadStartingCallbackAsync) {
      await this.imageUploadStartingCallbackAsync({ imageFiles: files });
      this.uploader.enqueueFiles(files);
    } else {
      this.uploader.enqueueFiles(files);
    }
  }

  /**
   * Tests if the content being dragged is an image or a video. This will
   * prevent text or image drag events within the window from triggering
   * the upload overlay by accident.
   */
  private isSupportedContent(e: DragEvent) {
    const FILE_REGEX = /^[video|image]/i;

    /**
     * dataTransfer.items is a Record<number, DataTransferItem>. This checks every value
     * in the map to ensure all of the given DataTransferItems are types that we should
     * respond to in our own drag handlers.
     */
    const items = Object.values(e.dataTransfer?.items ?? {});

    const hasItemsSupport = items.length > 0;
    // Safari hides the items list completely when dragging files, so we should just assume its valid
    // and allow them to attempt to upload.
    if (!hasItemsSupport) {
      return true;
    }

    return items.every(
      (item) => item.kind === 'file' && FILE_REGEX.test(item.type),
    );
  }

  /**
   * The default handler for the image uploading failure case.
   */
  private readonly defaultOnImageUploadFailed = (
    files: IImageUploaderFile[],
  ) => {
    files.forEach((file) => {
      this.addToastNotification({
        message: `The file "${file.name}" could not be uploaded ${
          file.message
            ? `: ${
                typeof file.message === 'string'
                  ? file.message
                  : 'Unknown upload error'
              }`
            : '.'
        }`,
        options: {
          extendedTimeOut: 0,
          timeOut: 0,
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
        title: 'Upload Failed!',
        type: ToastType.ERROR,
      });

      console.error(
        'crl8:',
        typeof file.message === 'string'
          ? file.message
          : JSON.stringify(file.message),
      );

      // Remove failed item from redux. Will eventually replace broadcasting 'universal-upload:content-failed' event.
      if (file.uuid) {
        this.$ngRedux.dispatch(
          UploadQueueActions.removeFailedItem({ itemId: file.uuid }),
        );
      }

      this.$rootScope.$broadcast('universal-upload:content-failed', file);
    });
  };

  /**
   * The default hanlder for the image uploading success case.
   */
  private readonly defaultOnImageUploadSucceeded = (
    file: IContentLibraryItem & { uuid: string },
  ) => {
    if (file.videoId && file.videoStatus) {
      PojoSet.add(this.processingVideosIdSet, file.id);
      this.addToastNotification({
        message: `Some videos may take time to process, but we'll let you know when they're ready.`,
        options: {
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
        title: 'Almost done!',
        type: ToastType.SUCCESS,
      });
    }

    // Remove uploaded item from redux. Will eventually replace broadcasting 'universal-upload:content-uploaded' event.
    this.$ngRedux.dispatch(
      UploadQueueActions.removeUploadedItem({ itemId: file.uuid }),
    );

    this.$rootScope.$broadcast('universal-upload:content-uploaded', file);

    this.videoProcessingWatcherDeregistration = this.$rootScope.$on(
      'scheduler:videos-finished-processing',
      (_event, itemIds: ItemId[]) => {
        const itemIdSet = PojoSet.from(itemIds);
        const processedVideos = PojoSet.intersection(
          this.processingVideosIdSet,
          itemIdSet,
        );
        if (PojoSet.toArray(processedVideos).length > 0) {
          this.processingVideosIdSet = PojoSet.difference(
            this.processingVideosIdSet,
            itemIdSet,
          );

          this.addToastNotification({
            type: ToastType.SUCCESS,
            title: 'Processing Complete!',
            message:
              'Your video has finished processing. Enjoy!<br><a href="/library">View in Media Library</a>',
            options: {
              ...LEGACY_TOAST_OPTIONS.DEFAULT_TOAST_OPTIONS,
              allowHtml: true,
              hideProgress: true,
              customIconClass: 'fa fa-check-circle-o fa-lg',
            },
          });
        }
      },
    );
  };

  /**
   * The default upload delegate, used to create a new queued-uploader in the case
   * that we aren't given one via a binding.
   */
  private readonly defaultUploadDelegate = (file: File) => {
    const ammendedFile: IImageUploaderFile = file;
    if (file.size > this.getMaxFileSizeBytes()) {
      ammendedFile.message = `File size is too large. Please select a file under ${this.getMaxFileSizeMb()}MB.`;
      return this.defaultOnImageUploadFailed([file]);
    }

    const uuid = createUUID();
    ammendedFile.uuid = uuid;

    // Add queued item to redux. Will eventually replace broadcasting 'universal-upload:add-queued-upload' event.
    this.$ngRedux.dispatch(UploadQueueActions.addQueuedItem({ itemId: uuid }));

    this.$rootScope.$broadcast('universal-upload:add-queued-upload', uuid);

    const collectionId =
      this.CollectionsSettingsService.getTenantedCollectionId();

    return this.gatewayMediaUploadApiService.uploadMediaToLibrary(
      ammendedFile,
      collectionId ? CollectionId(collectionId) : undefined,
    );
  };

  private readonly getMaxFileSizeMb = () => {
    return this.sessionVarsService.isFeatureEnabled(
      FeatureNames.LargeVideoUpload,
    )
      ? 2000
      : 200;
  };

  private readonly getMaxFileSizeBytes = () => {
    return this.getMaxFileSizeMb() * 1000000;
  };
}

angular.module('curalate').component('imageUploader', {
  bindings: {
    mode: '@',
    // Legacy interface: Callbacks for each event
    imageUploadStartingCallbackAsync: '&?',
    // Specify a QueuedUploader instance, which you can share between many <image-uploader> instances
    imageUploader: '&?',
    useNoopToastNotificationService: '<',
    name: '@?',
    buttonClass: '@?',
    legacyDarkOverlay: '@?',
  },
  template,
  controller: [
    '$ngRedux',
    '$document',
    '$rootScope',
    '$scope',
    '$templateCache',
    'CollectionsSettingsService',
    'QueuedUploaderService',
    'GatewayMediaUploadApiService',
    'SessionVarsService',
    ImageUploader,
  ],
});
